import { formatAppCurrency } from '@/renderer/lib/currency';
import type {
  InventoryDashboardMovementDailyRow,
  InventoryDashboardPurchaseDailyRow,
  InventoryDashboardRecentActivityRow,
  InventoryDashboardSnapshot,
} from '@/renderer/types/inventory';

export type { InventoryDashboardPeriodPreset } from '@/renderer/lib/utils';
export { getInventoryDashboardRange } from '@/renderer/lib/utils';

export const INVENTORY_DASHBOARD_PERIOD_OPTIONS = [
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 days' },
  { value: 'this_month', label: 'This month' },
] as const;

const INVENTORY_PRIMARY_HEX = '#15803d';
const INVENTORY_SECONDARY_HEX = '#166534';
const INVENTORY_MUTED_HEX = '#94a3b8';

export const INVENTORY_CHART_COLORS = {
  primary: INVENTORY_PRIMARY_HEX,
  secondary: INVENTORY_SECONDARY_HEX,
  muted: INVENTORY_MUTED_HEX,
  highlight: '#0f766e',
  warning: '#b45309',
};

export function formatCompactCurrency(value: number) {
  return formatAppCurrency(value, {
    notation: Math.abs(value) >= 1000000 ? 'compact' : 'standard',
    compactDisplay: 'short',
  });
}

export function formatCurrencyValue(value: number) {
  return formatAppCurrency(value);
}

export function formatQuantity(value: number) {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: value % 1 === 0 ? 0 : 2,
  }).format(value);
}

export function formatChartDate(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(new Date(`${value}T00:00:00`));
}

export function formatDateTime(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

export type InventoryPerformanceMode = 'stock_flow' | 'procurement';

export function buildPerformanceChartData(
  snapshot: InventoryDashboardSnapshot,
  mode: InventoryPerformanceMode,
) {
  const movementByDate = new Map(
    snapshot.movements.map((row) => [row.metric_date, row]),
  );
  const purchaseByDate = new Map(
    snapshot.purchases.map((row) => [row.metric_date, row]),
  );
  const dates = Array.from(
    new Set([
      ...snapshot.movements.map((row) => row.metric_date),
      ...snapshot.purchases.map((row) => row.metric_date),
    ]),
  ).sort((left, right) => left.localeCompare(right));

  return dates.map((metricDate) => {
    const movementRow = movementByDate.get(metricDate);
    const purchaseRow = purchaseByDate.get(metricDate);

    return {
      metricDate,
      label: formatChartDate(metricDate),
      inboundValue: movementRow?.movement_value_in ?? 0,
      outboundValue: movementRow?.movement_value_out ?? 0,
      inboundQuantity: movementRow?.quantity_in ?? 0,
      outboundQuantity: movementRow?.quantity_out ?? 0,
      purchaseValue: purchaseRow?.purchase_value ?? 0,
      paidValue: purchaseRow?.amount_paid ?? 0,
      activityCount:
        mode === 'stock_flow'
          ? (movementRow?.movement_count ?? 0)
          : (purchaseRow?.invoice_count ?? 0) +
            (purchaseRow?.payment_count ?? 0),
    };
  });
}

export function summarizeRecentActivity(
  rows: InventoryDashboardRecentActivityRow[],
) {
  const grouped = new Map<
    string,
    {
      key: string;
      label: string;
      count: number;
      totalAmount: number;
      latestOccurredAt: string;
    }
  >();

  rows.forEach((row) => {
    const key = row.activity_type;
    const label =
      row.activity_type === 'stock_movement'
        ? 'Stock movements'
        : row.activity_type === 'supplier_invoice'
          ? 'Supplier invoices'
          : 'Supplier payments';
    const existing = grouped.get(key);

    if (existing) {
      existing.count += 1;
      existing.totalAmount += Math.abs(row.amount ?? 0);
      if (row.occurred_at > existing.latestOccurredAt) {
        existing.latestOccurredAt = row.occurred_at;
      }
      return;
    }

    grouped.set(key, {
      key,
      label,
      count: 1,
      totalAmount: Math.abs(row.amount ?? 0),
      latestOccurredAt: row.occurred_at,
    });
  });

  return Array.from(grouped.values()).sort((left, right) =>
    right.latestOccurredAt.localeCompare(left.latestOccurredAt),
  );
}

export function getOverviewMetrics(snapshot: InventoryDashboardSnapshot) {
  const { overview } = snapshot;
  const purchaseValue = snapshot.purchases.reduce(
    (total, row) => total + row.purchase_value,
    0,
  );
  const quantityIn = snapshot.movements.reduce(
    (total, row) => total + row.quantity_in,
    0,
  );
  const quantityOut = snapshot.movements.reduce(
    (total, row) => total + row.quantity_out,
    0,
  );

  return {
    totalStockValue: overview?.total_stock_value ?? 0,
    lowStockItems: overview?.low_stock_item_count ?? 0,
    purchaseValue,
    purchaseInvoiceCount: snapshot.purchases.reduce(
      (total, row) => total + row.invoice_count,
      0,
    ),
    amountPaidInRange: snapshot.purchases.reduce(
      (total, row) => total + row.amount_paid,
      0,
    ),
    outstandingPayables: overview?.outstanding_payables ?? 0,
    activeItems: overview?.active_items ?? 0,
    activeSuppliers: overview?.active_suppliers ?? 0,
    quantityIn,
    quantityOut,
    stockLocations: overview?.stock_location_count ?? 0,
  };
}

export function buildLowStockHeadline(snapshot: InventoryDashboardSnapshot) {
  if (!snapshot.lowStock.length) {
    return 'All tracked items are above their reorder levels.';
  }

  const totalDeficit = snapshot.lowStock.reduce(
    (total, row) => total + row.deficit_quantity,
    0,
  );

  return `${snapshot.lowStock.length} items need attention across ${snapshot.locationValues.length || 1} locations. Deficit ${formatQuantity(totalDeficit)} units.`;
}

export function getMovementNarrative(row: InventoryDashboardMovementDailyRow) {
  if (row.purchase_receipt_count > 0) {
    return `${row.purchase_receipt_count} receipts posted`;
  }

  if (row.sale_consumption_count > 0) {
    return `${row.sale_consumption_count} recipe deductions posted`;
  }

  if (row.wastage_count > 0) {
    return `${row.wastage_count} wastage movements recorded`;
  }

  if (row.adjustment_count > 0) {
    return `${row.adjustment_count} stock adjustments posted`;
  }

  return `${row.movement_count} movement entries recorded`;
}

export function getPurchaseNarrative(row: InventoryDashboardPurchaseDailyRow) {
  if (row.invoice_count > 0 && row.payment_count > 0) {
    return `${row.invoice_count} invoices and ${row.payment_count} payments`;
  }

  if (row.invoice_count > 0) {
    return `${row.invoice_count} invoices posted`;
  }

  if (row.payment_count > 0) {
    return `${row.payment_count} supplier payments recorded`;
  }

  return 'No purchasing activity';
}
